import React, { ReactNode, useMemo, useState, useEffect } from 'react';
import useSWR from 'swr';

import { CartProps } from '~/types/cart';
import {
  CheckoutStateContextProps,
  CheckoutStepProps,
  StepErrorMessageType,
  SelectedPaymentType,
  StripeElementProps,
  CheckoutAPIContextProps,
  PaymentMethodProps,
} from '~/types/checkout';
import { STEP, COUNTRIES_REQUIRE_POSTAL_CODE } from '~/constants/checkout';
import { USER_CREDITS_URL } from '~/constants/accounts';
import { UserObjectProps } from '~/types/user';
import { useBilling, usePayment, useValidation } from '~/hooks/checkout';
import useUser from '~/lib/user';
import { CheckoutStateContext, CheckoutAPIContext } from '~/contexts/checkout';
import { useCart } from '~/lib/cart';

interface CheckoutProviderProps {
  initialCart?: CartProps;
  initialUser?: UserObjectProps;
  currentBillingCountry: string;
  setCurrentBillingCountry: (country: string) => void;
  children: ReactNode;
  initialPaymentMethods?: Array<PaymentMethodProps>;
  hideDistractions: boolean;
}

export const CheckoutProvider = ({
  children,
  initialCart,
  initialUser,
  currentBillingCountry,
  setCurrentBillingCountry,
  initialPaymentMethods,
  hideDistractions,
}: CheckoutProviderProps) => {
  const [step, setStep] = useState<CheckoutStepProps>(STEP.ACCOUNT);
  const [stepErrorMessageObject, setStepErrorMessageObject] =
    useState<StepErrorMessageType>({
      account: '',
      billing: '',
      payment: '',
      global: '',
    });
  const [currentSession, setCurrentSession] = useState<string>('');
  const [hasAppliedFastCheckout, setHasAppliedFastCheckout] =
    useState<boolean>(false);
  const [submittedPaymentMethod, setSubmittedPaymentMethod] =
    useState<SelectedPaymentType>(null);
  const [paymentElementData, setPaymentElementData] = useState<
    StripeElementProps | {}
  >({});

  const { user, session, isLoading: isUserLoading } = useUser(initialUser);
  const { cart } = useCart(initialCart);
  const { data: discountData, mutate: refreshDiscountData } =
    useSWR(USER_CREDITS_URL);
  const {
    defaultPaymentMethod,
    paymentMethods,
    selectedPaymentMethod,
    setSelectedPaymentMethod,
    submitPaymentMethod,
    isPaymentsLoaded,
  } = usePayment(user, initialPaymentMethods);
  const { submitBillingDetails, billingDetails, isBillingLoading } = useBilling(
    user,
    isUserLoading,
  );
  const { setStepValidation, getStepValidation, areAllStepsValid } =
    useValidation();

  // Clear any login errors if once the user is logged in
  // Set the step validation to true if the user is logged in (or false if not)
  useEffect(() => {
    if (user?.id && stepErrorMessageObject.account) {
      setStepErrorMessageObject((prev) => ({ ...prev, account: '' }));
    }

    if (!user?.id && getStepValidation(STEP.ACCOUNT)) {
      setStepValidation(STEP.ACCOUNT, false);
    } else if (user?.id && !getStepValidation(STEP.ACCOUNT)) {
      setStepValidation(STEP.ACCOUNT, true);
    }
  }, [
    user?.id,
    stepErrorMessageObject.account,
    getStepValidation,
    setStepValidation,
  ]);

  // Skip to the billing step if the user is logged in
  useEffect(() => {
    // Reset to the account step if no user
    if (!user && step !== STEP.ACCOUNT) {
      setStep(STEP.ACCOUNT);
    }

    if (!user || !session || session?.session_key === currentSession) return;

    if (session?.session_key) {
      setCurrentSession(session.session_key);
      setStep(STEP.BILLING);
    }
  }, [session, user, currentSession, step]);

  // Skip to the payment step if the billing details are valid
  useEffect(() => {
    if (!isPaymentsLoaded || hasAppliedFastCheckout) return;

    // Only apply fast checkout once (one time) after the page loads
    setHasAppliedFastCheckout(true);

    // Double check billing/payments are valid (should already be checked in BE)
    const isPostalCodeValid = COUNTRIES_REQUIRE_POSTAL_CODE.includes(
      `${cart?.billing_country}`,
    )
      ? !!cart?.billing_postal_code
      : true;
    const isTaxValid = cart?.billing_country === 'AU' ? !!cart?.tax_id : true;

    if (cart?.billing_country && isPostalCodeValid && isTaxValid) {
      setStep(STEP.PAYMENT);
      setStepValidation(STEP.BILLING, true);
    }
  }, [
    paymentMethods,
    setStepValidation,
    defaultPaymentMethod,
    hasAppliedFastCheckout,
    user,
    isPaymentsLoaded,
    cart?.billing_country,
    cart?.billing_postal_code,
    cart?.tax_id,
  ]);

  const checkoutStateProviderValue = useMemo(() => {
    return {
      billingDetails,
      cart,
      currentBillingCountry,
      isBillingLoading,
      isUserLoading,
      paymentElementData,
      selectedPaymentMethod,
      step,
      stepErrorMessageObject,
      submittedPaymentMethod,
      paymentMethods,
      defaultPaymentMethod,
      session,
      user,
      discountData,
      hideDistractions,
    };
  }, [
    billingDetails,
    cart,
    currentBillingCountry,
    isBillingLoading,
    isUserLoading,
    paymentElementData,
    selectedPaymentMethod,
    step,
    stepErrorMessageObject,
    submittedPaymentMethod,
    paymentMethods,
    defaultPaymentMethod,
    session,
    user,
    discountData,
    hideDistractions,
  ]) as CheckoutStateContextProps;

  const checkoutAPIProviderValue = useMemo(() => {
    const setStepErrorMessage = (step: CheckoutStepProps, message: string) => {
      setStepErrorMessageObject((prev) => ({ ...prev, [step]: message }));
    };

    return {
      areAllStepsValid,
      getStepValidation,
      setPaymentElementData,
      setStep,
      setStepErrorMessage,
      setStepValidation,
      setSubmittedPaymentMethod,
      submitBillingDetails,
      submitPaymentMethod,
      setCurrentBillingCountry,
      setSelectedPaymentMethod,
      refreshDiscountData,
    } as CheckoutAPIContextProps;
  }, [
    areAllStepsValid,
    getStepValidation,
    setCurrentBillingCountry,
    setStepValidation,
    submitBillingDetails,
    submitPaymentMethod,
    setSelectedPaymentMethod,
    refreshDiscountData,
  ]);

  return (
    <CheckoutStateContext.Provider value={checkoutStateProviderValue}>
      <CheckoutAPIContext.Provider value={checkoutAPIProviderValue}>
        {children}
      </CheckoutAPIContext.Provider>
    </CheckoutStateContext.Provider>
  );
};
